import { useState, useCallback, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DashboardChart, { ChartData } from "./DashboardChart";
import DynamicFilterPanel, { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import DropDownModel from "../../../components/Dropdowns/DropDownModel";
import Image from "../../../components/images/Image";
import DropdownFramer from "../../../components/framerMotion/DropdownFramer";

interface MerchantRankingData {
  merchant_name: string;
  merchant_code: string;
  transaction_count: number;
  total_stock_transfer_sales: number;
}

interface TopMerchantRankingApiResponse {
  data: MerchantRankingData[];
  message?: string;
}

// User role options - predefined list
const userRoleOptions = [
  { value: "MDST", label: "Master Distributor" },
  { value: "DST", label: "Distributor" },
  { value: "SDST", label: "Sub Distributor" },
  { value: "AGT", label: "Agent" },
  { value: "RET", label: "Retailer" }
];

const TopMerchantRanking: React.FC = () => {
  const api = useApi();
  
  // State for chart data
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [error, setError] = useState<string | undefined>(undefined);
  const [merchantData, setMerchantData] = useState<MerchantRankingData[]>([]); // Store merchant data for tooltip
  const chartLabelsRef = useRef<Array<{ name: string; code: string }>>([]); // Store merchant info for callback
  const hasInitialized = useRef(false);
  
  // Function to determine if label should be split into multiple lines
  // This function is stored in a ref so the Chart.js callback can access it
  const shouldUseMultiLineRef = useRef((name: string, code: string): boolean => {
    // Check screen width - on small/medium screens, use multi-line
    const currentWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (currentWidth < 1024) {
      return true;
    }
    
    // Check total character length - if combined text is too long, use multi-line
    const combinedLength = name.length + code.length + 3; // +3 for " ()"
    const threshold = 25; // Characters threshold for single line
    
    // Also check if name itself is long
    if (name.length > 20 || combinedLength > threshold) {
      return true;
    }
    
    return false;
  });
  
  // State for user role dropdown
  const [userRoleDropdownOpen, setUserRoleDropdownOpen] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState<string>("Master Distributor");
  const userRoleDropdownRef = useRef<HTMLDivElement>(null);
  const userRoleButtonRef = useRef<HTMLButtonElement>(null);
  const [userRoleDropdownPositionAbove, setUserRoleDropdownPositionAbove] = useState(false);
  
  // Internal search params for filters
  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams());

  // Dynamic filter configuration (User Role removed - now separate dropdown)
  const filterConfig: DynamicFilterConfig = {
    "Location": {
      label: "Location",
      type: "dropdown",
      required: false,
      searchKey: "location",
      subFilters: {
        country: {
          label: "Country",
          placeholder: "Select Country",
          searchKey: "country"
        },
        province: {
          label: "Province",
          placeholder: "Select Province",
          searchKey: "province"
        },
        region: {
          label: "Region",
          placeholder: "Select Region",
          searchKey: "region"
        }
      }
    },
    "Duration": {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: { 
        date: "date", 
        start: "start_date", 
        end: "end_date" 
      },
    }
  };

  // Function to process API data for chart
  const processDataForChart = useCallback((apiData: TopMerchantRankingApiResponse): ChartData => {
    if (!apiData.data || apiData.data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Get top 3 merchants (API should return sorted, but we'll ensure)
    const topMerchants = apiData.data.slice(0, 3);
    
    // Create labels array - we'll format them as multi-line in the callback
    // Store both the display format and the raw data for callback
    const labels = topMerchants.map(merchant => 
      `${merchant.merchant_name} (${merchant.merchant_code})`
    );
    
    // Store merchant info for multi-line formatting in callback
    chartLabelsRef.current = topMerchants.map(merchant => ({
      name: merchant.merchant_name,
      code: merchant.merchant_code
    }));
    
    // Create data array with total stock transfer sales amounts
    const salesAmountData = topMerchants.map(merchant => merchant.total_stock_transfer_sales);

    return {
      labels,
      datasets: [{
        label: "Total Stock Transfer Sales",
        data: salesAmountData,
        backgroundColor: 'rgba(139, 92, 246, 0.8)', // Purple color matching design
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
        minBarLength: 5, // The bar will always be at least 5px tall
      }]
    };
  }, []);

  // Function to fetch merchant ranking data
  const fetchMerchantRanking = useCallback(async (filters: { [key: string]: string | string[] | boolean | null }, userRole: string = "MDST") => {
    setLoading(true);
    setError(undefined);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add user role (required, default MDST)
      const role = filters.user_role as string || userRole || "MDST";
      params.append('user_role', role);
      
      // Add date range
      if (filters.start_date) {
        const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
        params.append('start_date', formattedStartDate);
      }
      if (filters.end_date) {
        const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
        params.append('end_date', formattedEndDate);
      }
      
      // Add location filters
      if (filters.country) {
        params.append('country', filters.country as string);
      }
      if (filters.province) {
        params.append('province', filters.province as string);
      }
      if (filters.region) {
        params.append('region', filters.region as string);
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `${endpoints.topMerchantEtopupRanking}?${params.toString()}`;
      const response = await api.getAPI(endpoint);
      
      if (response.error) {
        const errorMessage = response.data?.message || `Failed to fetch merchant ranking data for ${role}`;
        setError(errorMessage);
        setChartData({ labels: [], datasets: [] });
        return;
      }
      
      const apiData = response.data as TopMerchantRankingApiResponse;
      
      // Check if data is empty
      if (!apiData.data || apiData.data.length === 0) {
        // Set a specific "no data" error message
        setError('NO_DATA');
        setChartData({ labels: [], datasets: [] });
        return;
      }
      
      // Store full merchant data for tooltip
      setMerchantData(apiData.data);
      
      // Process the API data for chart
      const processedData = processDataForChart(apiData);
      console.log('Processed chart data:', processedData);
      console.log('Sales amounts:', apiData.data.map(m => ({ name: m.merchant_name, sales: m.total_stock_transfer_sales })));
      setChartData(processedData);
      setError(undefined);
      
    } catch (err) {
      console.error('Error fetching merchant ranking:', err);
      setError('An error occurred while fetching data');
      setChartData({ labels: [], datasets: [] });
    } finally {
      setLoading(false);
    }
  }, [api, processDataForChart]);

  // Handle user role change
  const handleUserRoleChange = useCallback((roleLabel: string) => {
    // If the same user role is selected, just close the dropdown and return
    if (selectedUserRole === roleLabel) {
      setUserRoleDropdownOpen(false);
      return;
    }
    
    const roleOption = userRoleOptions.find(r => r.label === roleLabel);
    if (roleOption) {
      setSelectedUserRole(roleLabel);
      setUserRoleDropdownOpen(false);
      
      // Get current filters and update with new user role
      const currentFilters: { [key: string]: string | string[] | boolean | null } = {
        user_role: roleOption.value
      };
      
      // Add existing filter values from searchParams
      if (searchParams.get('start_date')) {
        currentFilters.start_date = searchParams.get('start_date') as string;
      }
      if (searchParams.get('end_date')) {
        currentFilters.end_date = searchParams.get('end_date') as string;
      }
      if (searchParams.get('country')) {
        currentFilters.country = searchParams.get('country') as string;
      }
      if (searchParams.get('province')) {
        currentFilters.province = searchParams.get('province') as string;
      }
      if (searchParams.get('region')) {
        currentFilters.region = searchParams.get('region') as string;
      }
      
      fetchMerchantRanking(currentFilters, roleOption.value);
    }
  }, [searchParams, fetchMerchantRanking, selectedUserRole]);

  // Filter handler (without user role - it's handled separately)
  const handleFilterApply = useCallback((filters: { [key: string]: string | string[] | boolean | null }) => {
    console.log('Merchant Ranking Filters applied:', filters);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Clear existing filter parameters (except user_role)
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    newSearchParams.delete('country');
    newSearchParams.delete('province');
    newSearchParams.delete('region');
    
    // Process filters
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    // Get current user role from selectedUserRole
    const currentRoleOption = userRoleOptions.find(r => r.label === selectedUserRole);
    const currentUserRole = currentRoleOption?.value || "MDST";
    processedFilters.user_role = currentUserRole;
    newSearchParams.set('user_role', currentUserRole);
    
    // Handle Duration filter (dateRangeDropdown)
    if (filters.date) {
      // Add to internal search params
      newSearchParams.set('date', filters.date as string);
      
      // Map the date range selection to actual dates
      switch (filters.date) {
        case 'Today': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('day').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('day').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'Yesterday': {
          const yesterday = dayjs().subtract(1, 'day');
          processedFilters.start_date = yesterday.startOf('day').format('YYYY-MM-DD');
          processedFilters.end_date = yesterday.endOf('day').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'This Month': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('month').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('month').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'This Year': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('year').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('year').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'Custom': {
          if (filters.start_date) {
            const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            processedFilters.start_date = formattedStartDate;
            newSearchParams.set('start_date', formattedStartDate);
          }
          if (filters.end_date) {
            const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            processedFilters.end_date = formattedEndDate;
            newSearchParams.set('end_date', formattedEndDate);
          }
          break;
        }
      }
    }
    
    // Handle location filters
    if (filters.country) {
      processedFilters.country = filters.country as string;
      newSearchParams.set('country', filters.country as string);
    }
    if (filters.province) {
      processedFilters.province = filters.province as string;
      newSearchParams.set('province', filters.province as string);
    }
    if (filters.region) {
      processedFilters.region = filters.region as string;
      newSearchParams.set('region', filters.region as string);
    }
    
    // Update internal search params
    setSearchParams(newSearchParams);
    
    // Fetch data with processed filters
    fetchMerchantRanking(processedFilters, processedFilters.user_role as string);
  }, [searchParams, fetchMerchantRanking, selectedUserRole]);

  // Initialize with default data on component mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Set default filters (only user role, no date filters)
      const defaultFilters = {
        user_role: "MDST"
      };
      
      fetchMerchantRanking(defaultFilters, "MDST");
    }
  }, [fetchMerchantRanking]);

  // Function to calculate dropdown position synchronously
  const calculateDropdownPosition = useCallback(() => {
    if (userRoleButtonRef.current) {
      const rect = userRoleButtonRef.current.getBoundingClientRect();
      const dropdownMaxHeight = 240; // max-h-60 = 240px
      const gap = 4; // mt-1 = 4px
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Check if we should position above (not enough space below, but enough space above)
      const shouldPositionAbove = (spaceBelow < dropdownMaxHeight + gap) && spaceAbove > spaceBelow && spaceAbove >= dropdownMaxHeight;
      
      return shouldPositionAbove;
    }
    return false;
  }, []);

  // Handle dropdown toggle with position calculation before opening
  const handleUserRoleDropdownToggle = useCallback(() => {
    if (!userRoleDropdownOpen) {
      // Calculate position BEFORE opening to prevent flicker
      const shouldPositionAbove = calculateDropdownPosition();
      setUserRoleDropdownPositionAbove(shouldPositionAbove);
    }
    setUserRoleDropdownOpen(prev => !prev);
  }, [userRoleDropdownOpen, calculateDropdownPosition]);

  // Close user role dropdown when clicking or scrolling outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userRoleDropdownRef.current && !userRoleDropdownRef.current.contains(event.target as Node)) {
        setUserRoleDropdownOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      // Check if scroll is from inside the dropdown
      const target = event.target as HTMLElement;
      if (target && userRoleDropdownRef.current) {
        // Only close if scroll is NOT from inside the dropdown
        const isScrollFromDropdown = userRoleDropdownRef.current.contains(target);
        // Also check if it's scrolling within the dropdown's scrollable area
        const isDropdownScrollableArea = target.closest('.overflow-y-auto') && userRoleDropdownRef.current.contains(target.closest('.overflow-y-auto'));
        
        if (!isScrollFromDropdown && !isDropdownScrollableArea) {
          setUserRoleDropdownOpen(false);
        }
      } else {
        // If we can't determine the source, close the dropdown
        setUserRoleDropdownOpen(false);
      }
    };

    if (userRoleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scrolls
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [userRoleDropdownOpen]);


  return (
    <div className="w-full">
      <DashboardChart
        type="bar"
        data={chartData}
        height={550}
        showLegend={false}
        loading={loading}
        error={error}
        topbar={{
          title: "Top 3 Merchant Wise eTop Up Stock Transfer Ranking",
          rightContent: (
            <div className="flex items-center gap-3">
              {/* User Role Dropdown */}
              <DropDownModel 
                isOpen={userRoleDropdownOpen} 
                handleClose={() => setUserRoleDropdownOpen(false)}
              >
                <div ref={userRoleDropdownRef} className="relative">
                  <button
                    ref={userRoleButtonRef}
                    type="button"
                    onClick={handleUserRoleDropdownToggle}
                    className="bg-white h-10 px-4 py-2 border border-P_light_active rounded-lg text-subTitle outline-none focus:border-2 flex items-center justify-between gap-2 w-[120px] md:w-[140px] lg:w-[160px] xl:w-[180px] 2xl:w-[220px] min-w-[120px]"
                  >
                    <span className="text-primary_text truncate">{selectedUserRole}</span>
                    <DropdownFramer isDropdownVisible={userRoleDropdownOpen}>
                      <Image src="dropdown-down-arrow" />
                    </DropdownFramer>
                  </button>
                  {userRoleDropdownOpen && (
                    <div className={`absolute left-0 w-full bg-white border border-P_light_active rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto ${
                      userRoleDropdownPositionAbove ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}>
                      {userRoleOptions.map((role) => (
                        <div
                          key={role.value}
                          onClick={() => handleUserRoleChange(role.label)}
                          className={`p-2.5 text-subTitle cursor-pointer hover:bg-information_light hover:rounded-lg ${
                            selectedUserRole === role.label ? 'bg-information_light text-primary_text' : 'text-primary_text'
                          }`}
                        >
                          {role.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DropDownModel>
              
              {/* Filter Panel */}
              <div className="flex-shrink-0">
                <DynamicFilterPanel
                  config={filterConfig}
                  onApply={handleFilterApply}
                  onClear={() => {
                    console.log('Merchant Ranking filters cleared');
                    const newSearchParams = new URLSearchParams();
                    const currentRoleOption = userRoleOptions.find(r => r.label === selectedUserRole);
                    const currentUserRole = currentRoleOption?.value || "MDST";
                    newSearchParams.set('user_role', currentUserRole);
                    setSearchParams(newSearchParams);
                    const defaultFilters = {
                      user_role: currentUserRole
                    };
                    fetchMerchantRanking(defaultFilters, currentUserRole);
                  }}
                  loading={loading}
                  searchParams={searchParams}
                  customHeight="h-[160px]"
                />
              </div>
            </div>
          )
        }}
        options={{
          scales: {
            x: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.02)',
                lineWidth: 1
              },
              ticks: {
                font: {
                  size: 11,
                  family: 'Montserrat, sans-serif',
                  weight: 'bold'
                },
                color: '#666',
                maxRotation: 0,
                minRotation: 0,
                padding: 12, // Add padding for multi-line labels
                callback: function(_value: number | string, index: number) {
                  // Get merchant info from ref
                  const merchant = chartLabelsRef.current[index];
                  if (merchant) {
                    // Dynamically decide: single line or multi-line based on text length and screen size
                    if (shouldUseMultiLineRef.current(merchant.name, merchant.code)) {
                      // Return array for multi-line: name on first line, code on second line
                      return [merchant.name, `(${merchant.code})`];
                    } else {
                      // Return single line: name and code together
                      return `${merchant.name} (${merchant.code})`;
                    }
                  }
                  return '';
                }
              }
            },
            y: {
              display: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Total Amount of Transaction'
              },
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.02)',
                lineWidth: 1
              },
              ticks: {
                font: {
                  size: 12,
                  family: 'Montserrat, sans-serif',
                  weight: '600'
                },
                color: '#666',
                callback: function(value: number | string) {
                  const numValue = Number(value);
                  if (numValue >= 1000) {
                    const kValue = numValue / 1000;
                    // If it's a whole number, show without decimals (e.g., 1K, 2K, 5K)
                    // Otherwise show one decimal place (e.g., 1.5K, 2.3K)
                    const formattedValue = kValue % 1 === 0 ? kValue.toString() : kValue.toFixed(1);
                    return `AFA ${formattedValue}K`;
                  }
                  return `AFA ${numValue}`;
                }
              }
            }
          },
          elements: {
            bar: {
              borderWidth: 1
            }
          },
          // Make bars thinner based on count: 1 bar = thinnest, 2 bars = thinner, 3 bars = normal
          barPercentage: chartData.labels.length === 1 ? 0.2 : chartData.labels.length === 2 ? 0.3 : 0.5,
          categoryPercentage: chartData.labels.length === 1 ? 0.2 : chartData.labels.length === 2 ? 0.3 : 0.5,
          plugins: {
            tooltip: {
              enabled: true,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              displayColors: false, // Remove the color box/icon before labels
              callbacks: {
                title: function() {
                  // Set title to "Total Stock Transfer Sales"
                  return 'Total Stock Transfer Sales';
                },
                label: function(context: { dataIndex: number; parsed: { y: number }; label: string }) {
                  const index = context.dataIndex;
                  const merchant = merchantData[index];
                  const totalAmount = context.parsed.y || 0;
                  const transactionCount = merchant?.transaction_count || 0;
                  
                  // Format amount with K notation if >= 1000
                  let formattedAmount: string;
                  if (totalAmount >= 1000) {
                    const kValue = totalAmount / 1000;
                    const formattedValue = kValue % 1 === 0 ? kValue.toString() : kValue.toFixed(1);
                    formattedAmount = `AFA ${formattedValue}K`;
                  } else {
                    formattedAmount = `AFA ${totalAmount}`;
                  }
                  
                  // Format label: replace \n with space for tooltip
                  const tooltipLabel = context.label.replace(/\n/g, ' ');
                  
                  // Return merchant name with amount and transaction count
                  return [
                    `${tooltipLabel}: ${formattedAmount}`,
                    `No. of Transactions: ${transactionCount}`
                  ];
                }
              } as Record<string, unknown>
            } as Record<string, unknown>
          }
        }}
      />
    </div>
  );
};

export default TopMerchantRanking;
